import React, { useState, useEffect, useContext } from 'react';
import { languageContext } from '@/context/language.context';
import classes from './MoreOpportunitiesSection.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@/components/Button/Button';
import ODRS from '@/utils/api/odrs';
import { resourceHelpers, tagHelpers } from '@/utils/helpers';
import ReferButton from '@/components/Referrals/ReferButton/ReferButton';
import StarRating from '@/components/Resources/StarRating/StarRating';
import Bookmarks from '@/utils/api/odas/bookmarks';
import useAccount from '@/hooks/useAccount';
import Skeleton from 'react-loading-skeleton';
import { useRouter } from 'next/router';
import t from '@/utils/Language/OrganizationPage-translations';
import AddToCollectionButton from '@/components/AddToCollectionButton/AddToCollectionButton';
import Link from '@/helpers/Link';
import SaveButton from '@/components/SaveButton/SaveButton';
import { viewportContext } from '@/context/viewport.context';
import OpportunitiesMenuDropdown from './OpportunitiesMenuDropdown';

const INITIAL_DISPLAY_COUNT = 5;

// Lists an organization's opportunities. Used on both the organization page (all
// opportunities, with an "Add opportunity" action) and the opportunity page ("more services
// from this organization", excluding the opportunity being viewed).
const MoreOpportunitiesSection = ({ resource, isPro }) => {
  const [fetching, setFetching] = useState(true);
  const [opportunities, setOpportunities] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const { account } = useAccount();
  const { currentLanguage } = useContext(languageContext);
  const router = useRouter();
  const { isMobile } = useContext(viewportContext);
  const isOrganizationPage = resource?.resource_type === 'Organization';
  const isOpportunityPage = resource?.resource_type === 'Opportunity';

  useEffect(() => {
    const organizationId = isOpportunityPage ? resource?.organization?.id : resource?.id;
    ODRS.getResourceOpportunities(organizationId, { searcher: organizationId, titles_only: true, locale: currentLanguage }).then((resp) => {
      const loaded = resp?.data?.opportunities ?? [];
      // On an opportunity page, don't list the opportunity currently being viewed.
      setOpportunities(isOpportunityPage ? loaded.filter((el) => el.id !== resource?.id) : loaded);
      setFetching(false);
    });
  }, [currentLanguage, resource?.id]);

  const isBookmarked = (opportunity) => {
    return account?.bookmarks?.bookmarks?.find((el) => el.fetchable_id == opportunity.id);
  };

  const handleBookmarked = (opportunity) => {
    const params = { fetchableType: opportunity.resource_type, fetchableId: opportunity.id };
    if (isBookmarked(opportunity)) {
      Bookmarks.delete(params).then(() => {
        account.updateBookmarkCollection({
          action: 'remove',
          bookmark: { fetchable_type: opportunity.resource_type, fetchable_id: opportunity.id.toString() }
        });
      });
    } else {
      Bookmarks.addBookmark(params).then((response) => {
        account.updateBookmarkCollection({ action: 'add', bookmark: response.data.bookmark });
      });
    }
  };

  // The area icon's circular background is its icon color at 20% opacity. The color comes
  // from tagHelpers at render time, so it can't live in the stylesheet.
  const getIconBackgroundColor = (hexColor) => {
    const hex = parseInt(hexColor.replace(/^#/, ''), 16);
    const [r, g, b] = [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255];
    return `rgba(${r}, ${g}, ${b}, 0.2)`;
  };

  const bookmarkIcon = (opportunity) => ({
    style: isBookmarked(opportunity) ? 'fa' : 'fa-regular',
    font: 'fa-bookmark',
    position: 'left'
  });

  const displayedItems = isExpanded ? opportunities : opportunities?.slice(0, INITIAL_DISPLAY_COUNT);
  const shouldDisplayButton = opportunities?.length > INITIAL_DISPLAY_COUNT;

  if (fetching) {
    return (
      <>
        <Skeleton height={50} width={200} highlightColor="#ffffff" />
        <Skeleton height={300} highlightColor="#ffffff" />
      </>
    );
  }

  // The organization page still renders when empty — its header carries the "Add opportunity" action.
  if (isOpportunityPage && opportunities.length === 0) {
    return null;
  }

  return (
    <div className={classes.opportunitySectionContainer} id="org-opportunities">
      <div className={classes.opportunitySectionPanel}>
        <div className={classes.opportunitySectionHeader}>
          <div className={classes.opportunitySectionTitleWrapper}>
            <FontAwesomeIcon icon={['fas', isOrganizationPage ? 'rocket' : 'plus']} className={classes.opportunitySectionIcon} />
            <div>
              <h2 className={classes.opportunitySectionTitle}>
                {isOrganizationPage ? t('Opportunities') : t('More services from this organization')}
              </h2>
              {isOpportunityPage && <p className={classes.opportunitySectionSubtitle}>{resource?.organization?.name}</p>}
            </div>
          </div>
          {isOrganizationPage && (
            <Button
              type="default"
              text={t('Add opportunity')}
              icon={{ style: 'fas', font: 'plus', position: 'left' }}
              additionalClasses={classes.opportunityHeaderBtn}
              onButtonClick={() => router.push(`/org/${resource?.slug}/opp/new`)}
            />
          )}
        </div>
        <div className={classes.opportunityList}>
          {displayedItems?.map((opportunity) => {
            const areaTag = tagHelpers.icon(opportunity.areas[0]);
            return (
              <div className={classes.opportunityItem} key={opportunity.id}>
                <div className={classes.opportunityItemLeft}>
                  <div className={classes.opportunityItemIcon} style={{ backgroundColor: getIconBackgroundColor(areaTag.color) }}>
                    <FontAwesomeIcon icon={['fas', areaTag.class]} color={areaTag.color} />
                  </div>
                  <div className={classes.opportunityItemTitleWrapper}>
                    <Link href={`/opp/${opportunity.slug}`} className={classes.opportunityItemTitle}>
                      {opportunity.title}
                    </Link>
                    <div className={classes.opportunityItemRating}>
                      {resourceHelpers.isCurrentlyUnavailable(opportunity) && <p className={classes.unavailableText}>{t('Currently unavailable')}</p>}
                      {opportunity.rating > 0 && <StarRating stars={opportunity.rating} />}
                    </div>
                  </div>
                </div>
                {isPro && !isMobile && (
                  <div className={classes.opportunityItemRight}>
                    <ReferButton
                      buttonType="iconOnly"
                      buttonBorder={true}
                      buttonShape="pill"
                      resource={opportunity}
                      additionalClasses={classes.opportunityActionButton}
                      showButtonText={false}
                    />
                    <AddToCollectionButton
                      buttonType="iconOnly"
                      buttonBorder={true}
                      buttonShape="pill"
                      resource={opportunity}
                      additionalClasses={classes.opportunityActionButton}
                      showButtonText={false}
                    />
                    <Button
                      type="iconOnly"
                      text={''}
                      icon={bookmarkIcon(opportunity)}
                      additionalClasses={classes.opportunityActionButton}
                      onButtonClick={() => handleBookmarked(opportunity)}
                    />
                  </div>
                )}

                {isPro && isMobile && (
                  <OpportunitiesMenuDropdown>
                    <ReferButton
                      buttonType="link"
                      buttonBorder={true}
                      buttonShape="pill"
                      resource={opportunity}
                      additionalClasses={classes.opportunityMobileActionButton}
                      showButtonText={true}
                      showButtonTextOnMobile={true}
                    />
                    <AddToCollectionButton
                      buttonType="link"
                      buttonBorder={true}
                      buttonShape="pill"
                      resource={opportunity}
                      additionalClasses={classes.opportunityMobileActionButton}
                      showButtonText={true}
                      showButtonTextOnMobile={true}
                    />
                    <Button
                      type="link"
                      text={t('Bookmark')}
                      icon={bookmarkIcon(opportunity)}
                      additionalClasses={classes.opportunityMobileActionButton}
                      onButtonClick={() => handleBookmarked(opportunity)}
                    />
                  </OpportunitiesMenuDropdown>
                )}

                {!isPro && (
                  <div className={classes.opportunityItemRight}>
                    <SaveButton
                      buttonType="iconOnly"
                      buttonBorder={false}
                      buttonShape="pill"
                      resource={opportunity}
                      additionalClasses={classes.opportunityActionButton}
                      secondOrigin={null}
                      showButtonText={false}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {shouldDisplayButton && (
          <div className={classes.opportunityShowMoreWrapper}>
            <Button
              type="link"
              text={isExpanded ? t('Hide opportunities') : t('Show all opportunities')}
              onButtonClick={() => setIsExpanded(!isExpanded)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MoreOpportunitiesSection;
